/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio e nomes de categoria do backend (U02) */
import { msg } from '@lingui/core/macro';
import { defineEnum } from '@/shared/patterns/define-enum';

/**
 * `categoryNameOptions` (`types/creditProductReadModel.ts:331-357`): o valor vai ao fio e o `code`
 * vira a carteira (`InformationTabCreditProduct.tsx:175-179`). Nome de categoria é dado do backend,
 * mostrado como veio (U33), na ordem alfabética do legado.
 */
export const CATEGORY_OPTIONS: readonly { label: string; value: string; code: number }[] = [
  { label: 'CCB', value: 'CCB', code: 8 },
  { label: 'CCB CLEAN', value: 'CCB CLEAN', code: 9 },
  { label: 'CCB FINANCIAMENTO VE', value: 'CCB FINANCIAMENTO VE', code: 10 },
  { label: 'CCB PRICE', value: 'CCB PRICE', code: 11 },
  { label: 'CDC', value: 'CDC', code: 12 },
  { label: 'CDC FGTS', value: 'CDC FGTS', code: 13 },
  { label: 'COMPRA DE DÍVIDA', value: 'COMPRA DE DÍVIDA', code: 8 },
  { label: 'CONSIGNADO PRIVADO', value: 'CONSIGNADO PRIVADO', code: 14 },
  { label: 'CONSIGNADO PRIVADO CDC', value: 'CONSIGNADO PRIVADO CDC', code: 21 },
  { label: 'CONSIGNADO PRIVADO LEILÃO', value: 'CONSIGNADO PRIVADO LEILÃO', code: 22 },
  { label: 'CONSIGNADO PÚBLICO', value: 'CONSIGNADO PÚBLICO', code: 15 },
  { label: 'CAR EQUITY', value: 'CAR EQUITY', code: 24 },
  { label: 'FINANCIAMENTO DE VEÍCULO', value: 'FINANCIAMENTO DE VEICULO', code: 25 },
  { label: 'CARTÃO BENEFÍCIO', value: 'CARTÃO BENEFÍCIO', code: 15 },
  { label: 'CRÉDITO PESSOAL', value: 'CRÉDITO PESSOAL', code: 20 },
  { label: 'DESCONTO', value: 'DESCONTO', code: 19 },
  { label: 'FGTS', value: 'FGTS', code: 6 },
  { label: 'FGTS COM SEGURO', value: 'FGTS COM SEGURO', code: 6 },
  { label: 'FINANCIAMENTO ESTUDANTIL', value: 'FINANCIAMENTO ESTUDANTIL', code: 18 },
  { label: 'CONSIGNADO INSS', value: 'CONSIGNADO INSS', code: 23 },
  { label: 'NOTA COMERCIAL', value: 'NOTA COMERCIAL', code: 1 },
  { label: 'PROCESSO TRABALHISTA', value: 'PROCESSO TRABALHISTA', code: 16 },
  { label: 'VEÍCULO', value: 'VEÍCULO', code: 17 },
  { label: 'ANTECIPAÇÃO DE RECEBÍVEIS', value: 'ANTECIPAÇÃO DE RECEBÍVEIS', code: 26 },
  { label: 'HOME EQUITY', value: 'HOME EQUITY', code: 27 },
].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

/** `SCC_CIP_CATEGORIES` (`utils/constants/warrantyRegistrationOfficeOptions.ts:13`). */
export const SCC_CIP_CATEGORIES = ['CONSIGNADO PÚBLICO', 'CARTÃO BENEFÍCIO'];
/** `InformationTabCreditProduct.tsx:121-122`. */
export const VEHICLE_CATEGORIES = ['CAR EQUITY', 'FINANCIAMENTO DE VEICULO'];

/** `ammTypeOptions` (`InformationTabCreditProduct.tsx:79-87`), em ordem alfabética do rótulo. */
export const AMORTIZATION = defineEnum(
  {
    Price: { label: msg`PRICE (B3)` },
    SAC: { label: msg`SAC (B3)` },
    PriceCoefficient: { label: msg`PRICE Coeficiente` },
    FGTS: { label: msg`FGTS` },
    StudentLoan: { label: msg`Financiamento estudantil` },
    Discount: { label: msg`Desconto` },
    ReceivablesAdvance: { label: msg`Antecipação de Recebíveis` },
  },
  { order: 'label' },
);

export const INSTRUMENT = defineEnum({
  CommercialPapper: { label: msg`Nota Comercial` },
  CCB: { label: msg`CCB` },
  PublicPayrollLoan: { label: msg`Consignado Público` },
  CreditAssignment: { label: msg`Cessão de créditos` },
});

export const VIEW_TYPE = defineEnum({
  TopToBottom: { label: msg`Aberto` },
  BottomToTop: { label: msg`Fechado` },
});

/** `termStatusOptionsList` (`creditProductReadModel.ts:359-364`). */
export const TERM_STATUS = defineEnum({
  Current: { label: msg`Vigente` },
  Future: { label: msg`Futura` },
  Expired: { label: msg`Expirada` },
  ProposalPending: { label: msg`Propostas Pendentes` },
});

/** Select numérico (`InformationTabCreditProduct.tsx:1243-1247`). */
export const SIGNATURE_TYPE = defineEnum(
  { '0': { label: msg`Email` }, '1': { label: msg`Sms` }, '2': { label: msg`Whatsapp` } },
  { numeric: true, order: ['0', '1', '2'] },
);

/** `ESignaturePortalProvider` (`services/creditProduct/enums/index.ts:28-34`). */
export const PORTAL_PROVIDER = defineEnum(
  {
    '0': { label: msg`RBM` },
    '1': { label: msg`ClickSign` },
    '2': { label: msg`QuickSoft` },
    '3': { label: msg`UnicoCheck` },
    '4': { label: msg`UnicoCloud` },
  },
  { numeric: true, order: ['0', '1', '2', '3', '4'] },
);

/** `optionsSignatureValidation` (`InformationTabCreditProduct.tsx:209-229`): só RBM (0) e ClickSign (1). */
export const SIGNATURE_VALIDATION = defineEnum({
  EletronicSignature: { label: msg`Assinatura Eletrônica` },
  EletronicSignatureAndDocument: { label: msg`Assinatura Eletrônica + Documento` },
  SelfieSignature: { label: msg`Assinatura com Selfie` },
  DigitalCertificate: { label: msg`Certificado Digital` },
  SelfieDocumentSignature: { label: msg`Selfie + Documento Oficial` },
  FacialBiometrics: { label: msg`Biometria Facial` },
});
export const SIGNATURE_VALIDATION_BY_PROVIDER: Readonly<
  Record<string, readonly (typeof SIGNATURE_VALIDATION.values)[number][]>
> = {
  '0': ['EletronicSignature', 'SelfieSignature', 'DigitalCertificate'],
  '1': [
    'EletronicSignature',
    'EletronicSignatureAndDocument',
    'SelfieSignature',
    'DigitalCertificate',
    'SelfieDocumentSignature',
    'FacialBiometrics',
  ],
};

/** `WARRANTY_REGISTRATION_OFFICE_OPTIONS` (`utils/constants/warrantyRegistrationOfficeOptions.ts`). */
export const WARRANTY_OFFICE = defineEnum({
  None: { label: msg`Nenhum` },
  DataPrevCreditoTrabalhador: { label: msg`Dataprev - Crédito do Trabalhador` },
  Fgts: { label: msg`FGTS` },
  Zetra: { label: msg`Zetra` },
  Siape: { label: msg`SIAPE` },
  Others: { label: msg`Outros` },
  SemearInss: { label: msg`Banco Semear - INSS` },
  CarEquity: { label: msg`Car Equity` },
  SccCip: { label: msg`SCC - CIP` },
});

/** `TEMPLATE_TYPE_OPTIONS` (`CreditProductTemplateForm.tsx:13-20`) na ordem do select. */
export const TEMPLATE_TYPE = defineEnum(
  {
    Draft: { label: msg`Minuta` },
    RegistrationDocument: { label: msg`Documento de Averbação` },
    OperationHistoryDocument: { label: msg`Documento histórico da Operação` },
    InsuranceCancellationDocument: { label: msg`Documento de Cancelamento de Seguro` },
    CetCalculationStatement: { label: msg`Demonstrativo de Cálculo da CET` },
    Authorization: { label: msg`Autorização` },
  },
  {
    order: [
      'Draft',
      'RegistrationDocument',
      'OperationHistoryDocument',
      'InsuranceCancellationDocument',
      'CetCalculationStatement',
      'Authorization',
    ],
  },
);
/** `TEMPLATE_TYPE_BY_CODE` (`CreditProductTemplateTab.tsx:60-67`). */
export const TEMPLATE_TYPE_BY_CODE: Readonly<Record<number, string>> = {
  0: 'Draft',
  1: 'RegistrationDocument',
  2: 'OperationHistoryDocument',
  3: 'InsuranceCancellationDocument',
  4: 'Authorization',
  5: 'CetCalculationStatement',
};

export const COMMISSION_TYPE = defineEnum({
  Absolute: { label: msg`Valor Fixo` },
  Percentage: { label: msg`Percentual` },
});
export const COMMISSION_BASE = defineEnum({
  InitialValue: { label: msg`Valor inicial da dívida` },
  TaxFreeInitialValue: { label: msg`Valor inicial da dívida menos IOF` },
  RequestedValue: { label: msg`Valor solicitado pelo tomador` },
});
export const CHARGE_FROM = defineEnum({
  All: { label: msg`Todos` },
  NewCustomers: { label: msg`Novos clientes` },
  RegisteredCustomers: { label: msg`Clientes recorrentes` },
});

export const PERSON_TYPE = defineEnum({
  Natural: { label: msg`Pessoa Física` },
  Legal: { label: msg`Pessoa Jurídica` },
});

/** Rótulo por valor técnico (U33); fora do mapa, o `name` do `GET /Enum/FileType`. */
export const FILE_TYPE = defineEnum({
  Document: { label: msg`Documento` },
  Authorization: { label: msg`Autorização` },
  DriversLicense: { label: msg`Carteira de Habilitação` },
  WorkCard: { label: msg`Carteira de Trabalho` },
  ResidenceProofCompany: { label: msg`Comprovante de Residência Empresa` },
  ResidenceProofPartners: { label: msg`Comprovante de Residência PF` },
  Draft: { label: msg`Minuta` },
  SocialContract: { label: msg`Contrato Social` },
  RegistrationNumber: { label: msg`CPF` },
  IdentificationDocumentWithPhoto: { label: msg`Documento de Identificação com Foto` },
  BankStatement: { label: msg`Extrato Bancário` },
  Others: { label: msg`Outros` },
  SignedContract: { label: msg`Contrato Assinado` },
  Regulation: { label: msg`Regulamento` },
});

/** `TypeOfRelationship`: fora do mapa, o `name` do enum da API. */
export const RELATION = defineEnum({
  Proxy: { label: msg`Procurador` },
  Partner: { label: msg`Sócio` },
  Spouse: { label: msg`Cônjuge` },
  Witness: { label: msg`Testemunha` },
  Creditor: { label: msg`Credor` },
  Beneficiary: { label: msg`Beneficiário` },
  BillingAgent: { label: msg`Agente de cobrança` },
});

/** `creditRulesDescriptions` (`types/creditRulesTypes.ts:4-50`), pela chave numérica. */
export const CREDIT_RULE = defineEnum(
  {
    '0': { label: msg`Aprovação automática` },
    '1': { label: msg`Motor de crédito` },
    '2': { label: msg`Banco Central SCR [descontinuado]` },
    '3': { label: msg`Mínimo de funcionários` },
    '4': { label: msg`Instituições Financeiras` },
    '5': { label: msg`Banco Central SCR - Limite bruto` },
    '6': { label: msg`Banco Central SCR - Limite por % de salário líquido` },
    '7': { label: msg`Boa vista - Score de crédito` },
    '8': { label: msg`Boa vista - Renda presumida` },
    '10': { label: msg`Capital Social` },
    '11': { label: msg`Natureza Jurídica e Regime Fiscal` },
    '12': { label: msg`Regularidade FGTS` },
    '13': { label: msg`Mínimo de funcionários ou faturamento mínimo` },
    '14': { label: msg`Prazo máximo de operação vs Tempo de casa do tomador` },
    '15': { label: msg`Código Brasileiro de Ocupações (CBO)` },
    '16': { label: msg`Tempo de existência da empresa` },
    '17': { label: msg`Bloqueio por Inadimplência` },
    '18': { label: msg`Salário/Faturamento mínimo` },
    '19': { label: msg`Lista Negativa` },
    '20': { label: msg`Lista Positiva` },
    '22': { label: msg`Dívida Ativa da União` },
    '23': { label: msg`Situação na Receita Federal (Ativo ou Inativo)` },
    '24': { label: msg`Mínimo de funcionários ou faturamento mínimo - Grupo empresarial` },
    '25': { label: msg`Salário Máximo` },
    '26': { label: msg`Regra de bloqueio de operação repetidas` },
    '27': { label: msg`Mandado de prisão` },
    '28': { label: msg`Débitos Trabalhistas` },
    '29': { label: msg`Pessoa jurídica em situação de Falência ou Recuperação judicial` },
    '31': { label: msg`Taxa de repasse` },
    '32': { label: msg`Dívida de crédito do trabalhador` },
    '33': { label: msg`Limite de vínculos empregatícios` },
    '34': { label: msg`Limite diário de operações por empregador` },
    '35': { label: msg`Valor máximo para aprovação de crédito` },
    '36': { label: msg`Contratos por porte de empresa` },
    '37': { label: msg`Boa vista - Score de Crédito PJ` },
    '38': { label: msg`SCR zerado - Pessoa Física` },
    '39': { label: msg`SCR zerado - Pessoa Jurídica` },
    '40': { label: msg`Bloqueio por estado do empregador / porte` },
  },
  { numeric: true },
);

/** `ComplianceRulesTypeDescriptions` (`types/complianceRulesTypes.ts:25-41`). */
export const COMPLIANCE_RULE = defineEnum(
  {
    '0': { label: msg`Aprovação automática` },
    '1': { label: msg`Bloqueio de CPF` },
    '2': { label: msg`Blacklist UY3` },
    '3': { label: msg`Regularidade de CPF` },
    '4': { label: msg`Validação de nome` },
    '5': { label: msg`Score de risco` },
    '6': { label: msg`Motor de compliance` },
    '7': { label: msg`KYC` },
    '8': { label: msg`R6` },
    '9': { label: msg`Aprovações recentes` },
    '10': { label: msg`CNAE` },
    '11': { label: msg`SCR - Banco Central` },
    '12': { label: msg`Bloqueio de transferências/Pix – Bloqueio por Instituição` },
    '13': { label: msg`Bloqueio de transferências/Pix – Bloqueio por agência` },
  },
  { numeric: true },
);

/** Ação da regra: `Reject = 0` rejeita; qualquer outro valor é "Aprovação manual" (`creditRulesTypes.ts:73-75`). */
export const RULE_ACTION = defineEnum(
  { '0': { label: msg`Rejeitar` }, '1': { label: msg`Aprovação manual` } },
  { numeric: true },
);
